import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { userPreferencesApi } from '@/api/userPreferencesApi'
import { useAuth } from '@/providers/AuthProvider'
import { TABLE_PREFS_QUERY_KEY } from '@/providers/TableColumnPrefsProvider'

const WRITE_DEBOUNCE_MS = 600

/** A value and the key it belongs to, so it can never be read or saved under another key. */
type Keyed<T> = { key: string; value: T }

/**
 * One `UserPreference` value, read from the shared `GET /preferences` map and
 * written back after a short quiet period (z8nrz7eqyf).
 *
 * The same storage path as `useHomeLayouts` and `useBoardPrefs`: changes apply
 * at once, are saved after a quiet period, flushed with `keepalive` when the
 * page goes away, and not rolled back on failure. A value `isDefault` calls
 * default is deleted rather than stored.
 *
 * `key` is null until there is a tenant to store under. Pass `coerce` and
 * `isDefault` as stable references (module functions), or the value is
 * recomputed every render.
 *
 * Each write replaces the whole value, so two open tabs edit last-writer-wins.
 */
export function useStoredPreference<T>(
  key: string | null,
  coerce: (raw: unknown) => T,
  isDefault: (value: T) => boolean,
) {
  const { isAuthenticated } = useAuth()
  const queryClient = useQueryClient()
  const { data: serverMap, isLoading } = useQuery({
    queryKey: TABLE_PREFS_QUERY_KEY,
    queryFn: userPreferencesApi.getAll,
    enabled: isAuthenticated,
    staleTime: Infinity,
    retry: 1,
  })

  const [local, setLocal] = useState<Keyed<T> | null>(null)
  // Set when the last save was refused, so the page can say so instead of the
  // change quietly vanishing on the next refresh.
  const [saveFailed, setSaveFailed] = useState(false)
  const hasLocal = !!local && local.key === key
  const value = useMemo(
    () => (hasLocal ? local!.value : coerce(serverMap && key ? serverMap[key] : undefined)),
    [hasLocal, local, serverMap, key, coerce],
  )

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Carries its key: a save still waiting when the tenant changes goes to the
  // tenant it was made in, not the one now selected.
  const pending = useRef<Keyed<T> | null>(null)

  const flush = useCallback(
    (unloading = false) => {
      if (timer.current) {
        clearTimeout(timer.current)
        timer.current = null
      }
      const next = pending.current
      if (!next) return
      pending.current = null
      const opts = unloading ? { keepalive: true } : undefined
      const keep = !isDefault(next.value)
      const save = keep
        ? userPreferencesApi.set(next.key, next.value, opts)
        : userPreferencesApi.remove(next.key, opts)
      void save
        .then(() => {
          setSaveFailed(false)
          queryClient.setQueryData<Record<string, unknown>>(TABLE_PREFS_QUERY_KEY, prev => {
            const map = { ...(prev ?? {}) }
            if (keep) map[next.key] = next.value
            else delete map[next.key]
            return map
          })
        })
        .catch(error => {
          setSaveFailed(true)
          console.error(`[preferences] could not save "${next.key}"`, error)
        })
    },
    [queryClient, isDefault],
  )

  const update = useCallback(
    (next: T) => {
      if (!key) return
      // A save for another key still waiting goes out now, under its own key.
      if (pending.current && pending.current.key !== key) flush()
      setLocal({ key, value: next })
      pending.current = { key, value: next }
      if (timer.current) clearTimeout(timer.current)
      timer.current = setTimeout(() => flush(), WRITE_DEBOUNCE_MS)
    },
    [flush, key],
  )

  useEffect(() => {
    const onPageHide = () => flush(true)
    const onHidden = () => {
      if (document.visibilityState === 'hidden') flush(true)
    }
    window.addEventListener('pagehide', onPageHide)
    document.addEventListener('visibilitychange', onHidden)
    return () => {
      window.removeEventListener('pagehide', onPageHide)
      document.removeEventListener('visibilitychange', onHidden)
      flush(false)
    }
  }, [flush])

  return {
    value,
    update,
    /** The last save was refused; what is on screen is not stored. */
    saveFailed,
    /** Until the stored value arrives, show nothing rather than flashing the default. */
    isLoading: isAuthenticated && isLoading && !hasLocal,
    /**
     * Whether the stored value is known and has a key to belong to. Every write
     * replaces the whole value, so writing before the read lands (or after it
     * failed) would wipe what the user already has.
     */
    ready: key !== null && (hasLocal || !!serverMap),
  }
}
